import { createBrowserRouter } from 'react-router';
import { lazy, Suspense } from 'react';
import MainLayout from '../components/layouts/MainLayout';
import AdminLayout from '../components/layouts/AdminLayout';
import GuestRoute from '../components/authenticators/GuestRoute';
import ProtectedRoute from '../components/authenticators/ProtectedRoute';
import ErrorPage from '../components/pages/ErrorPage';
import FullPageSpinner from '@/components/reusables/FullPageSpinner';
import DashboardPage from '@/components/pages/superadmin/DashboardPage';
import DashboardPageStoreOwner from '@/components/pages/storeowner/DashboardPage';
import SomethingWentWrong from '@/components/reusables/error/SomethingWentWrong';

const LoginPage = lazy(() => import('@/components/pages/LoginPage'));
const BlankPage = lazy(() => import('@/components/pages/BlankPage'));
const ProfilePage = lazy(() => import('@/components/pages/superadmin/ProfilePage'));
const QRScanActivityPage = lazy(() => import('@/components/pages/storeowner/QRScanActivity'));
const ProductCatalogPage = lazy(() => import('@/components/pages/storeowner/ProductCatalogPage'));
const ImportHistoryOwnerPage = lazy(
  () => import('@/components/pages/storeowner/ImportHistoryOwnerPage')
);
const ImportHistoryManagerPage = lazy(
  () => import('@/components/pages/storemanager/ImportHistoryManagerPage')
);
const ImportHistoryPageSP = lazy(() => import('@/components/pages/superadmin/ImportHistoryPage'));
const ProdductImportPage = lazy(() => import('@/components/pages/storeowner/ProductImportPage'));
const LandingPageConfiguration = lazy(
  () => import('@/components/pages/storeowner/LandingPageConfiguration')
);
const LandingPageConfigurationForManager = lazy(
  () => import('@/components/pages/storemanager/LandingPageConfiguration')
);
const DashboardPageStoreManager = lazy(
  () => import('@/components/pages/storemanager/DashboardPage')
);
const QRScanActivityPageSM = lazy(() => import('@/components/pages/storemanager/QRScanActivity'));
const EmployeeManagementPage = lazy(
  () => import('@/components/pages/storeowner/EmployeeManagementPage')
);
const ProductCatalogPageSM = lazy(() => import('@/components/pages/storemanager/ProductCatalog'));
const EmployeeManagementPageSM = lazy(
  () => import('@/components/pages/storemanager/EmployeeManagementPage')
);
const DashboardPageEmployee = lazy(() => import('@/components/pages/employee/DashboardPage'));
const QRScanActivityPageEmployee = lazy(() => import('@/components/pages/employee/QRScanActivity'));
const ProductCatalogEmployee = lazy(() => import('@/components/pages/employee/ProductCatalog'));
const QrScanAnalyticsPage = lazy(() => import('@/components/pages/storeowner/QrScanAnalyticsPage'));
const QrScanAnalyticsPageForManager = lazy(
  () => import('@/components/pages/storemanager/QrScanAnalyticsPage')
);
const ReportForEmployee = lazy(() => import('@/components/pages/employee/Reports'));
const LandingPreview = lazy(() => import('@/components/pages/storeowner/LandingPreview'));
const LandingPreviewManager = lazy(() => import('@/components/pages/storemanager/LandingPreview'));
const AddQrCodeQueue = lazy(() => import('@/components/pages/storeowner/AddQrCodeQueue'));
const RePrintQrCodes = lazy(() => import('@/components/pages/storeowner/RePrintQrCodes'));
const RePrintQrCodesByManager = lazy(
  () => import('@/components/pages/storemanager/RePrintQrCodes')
);
const PrintHistory = lazy(() => import('@/components/pages/storeowner/PrintHisory'));
const BatchHistory = lazy(() => import('@/components/pages/storeowner/BatchHistory'));
const PrintHistoryManager = lazy(
  () => import('@/components/pages/storemanager/PrintHistoryManagerPage')
);
const BatchHistoryManager = lazy(
  () => import('@/components/pages/storemanager/BatchHistoryManagerPage')
);
const QuoteAndLeadPage = lazy(() => import('@/components/pages/storeowner/QuoteAndLeadPage'));
const QuoteAndLeadPageManager = lazy(
  () => import('@/components/pages/storemanager/QuoteAndLeadPage')
);
const BillingPage = lazy(() => import('@/components/pages/storeowner/Billing'));
const NotificationsAlertsPage = lazy(
  () => import('@/components/pages/superadmin/NotificationsAlerts')
);
const QRCodeManagementPage = lazy(
  () => import('@/components/pages/storeowner/QRCodeManagementPage')
);
const QRCodeManagementPageManager = lazy(
  () => import('@/components/pages/storemanager/QRCodeManagementPage')
);
const QRCodeManagementPageEmployee = lazy(
  () => import('@/components/pages/employee/QRCodeManagementPage')
);
const OwnerManagement = lazy(() => import('@/components/pages/superadmin/OwnerManagement'));
const VendorManagement = lazy(() => import('@/components/pages/superadmin/VendorManagementPage'));
const ManufacturerPage = lazy(() => import('@/components/pages/superadmin/ManufacturerPage'));
const StoreManagementPage = lazy(() => import('@/components/pages/superadmin/StoreManagementPage'));
const ProductCategoryManagementPage = lazy(
  () => import('@/components/pages/storeowner/ProductCategoryManagementPage')
);
const ProductVendorManagementPage = lazy(
  () => import('@/components/pages/storeowner/ProductVendorManagementPage')
);
const ProductTagManagementPage = lazy(
  () => import('@/components/pages/storeowner/ProductTagManagementPage')
);
const QRCodeGeneratorSettingsPage = lazy(
  () => import('@/components/pages/storeowner/QRCodeGeneratorSettingsPage')
);
const QRCodeGeneratorSettingsPageManager = lazy(
  () => import('@/components/pages/storemanager/QRCodeGeneratorSettingsPage')
);
const ProductDetailsPage = lazy(() => import('@/components/pages/customer/ProductDetailsPage'));
const AddQrCodeQueueManagerPage = lazy(
  () => import('@/components/pages/storemanager/AddQrCodeQueueManagerPage')
);
const PaymentMethodListPage = lazy(
  () => import('@/components/pages/superadmin/PaymentMethodListPage')
);
const AddPaymentMethodPage = lazy(
  () => import('@/components/pages/superadmin/AddPaymentMethodPage')
);
const SubscriptionsListPage = lazy(
  () => import('@/components/pages/superadmin/SubscriptionsListPage')
);
const AddSubscriptionPage = lazy(() => import('@/components/pages/superadmin/AddSubscriptionPage'));
const ProductImportPageM = lazy(() => import('@/components/pages/storemanager/ProductImportPageM'));
const GenieRequestsPage = lazy(() => import('@/components/pages/superadmin/GenieRequestsPage'));
const UploadedFilesPage = lazy(() => import('@/components/pages/superadmin/UploadedFilesPage'));
const ProductImportPageSP = lazy(() => import('@/components/pages/superadmin/ProductImportPageSP'));

const router = createBrowserRouter([
  {
    Component: () => (
      <Suspense fallback={<FullPageSpinner />}>
        <MainLayout />
      </Suspense>
    ),
    children: [
      // Guest Routes
      {
        Component: GuestRoute,
        children: [
          { index: true, Component: LoginPage, errorElement: <SomethingWentWrong /> },
          {
            path: 'product/:viewType/:productId',
            Component: ProductDetailsPage,
            errorElement: <SomethingWentWrong />,
          },
        ],
      },
    ],
  },
  {
    Component: () => (
      <Suspense fallback={<FullPageSpinner />}>
        <AdminLayout />
      </Suspense>
    ),
    children: [
      {
        Component: ProtectedRoute,
        children: [
          {
            path: 'superadmin',
            children: [
              { path: 'dashboard', Component: DashboardPage, errorElement: <SomethingWentWrong /> },
              { path: 'profile', Component: ProfilePage, errorElement: <SomethingWentWrong /> },
              {
                path: 'owner-management',
                Component: OwnerManagement,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'alerts',
                Component: NotificationsAlertsPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'store-management',
                children: [
                  {
                    index: true,
                    Component: StoreManagementPage,
                    errorElement: <SomethingWentWrong />,
                  },
                  {
                    path: 'manufacturers',
                    Component: ManufacturerPage,
                    errorElement: <SomethingWentWrong />,
                  },
                ],
              },
              {
                path: 'vendor-management',
                Component: VendorManagement,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'genie-requests',
                Component: GenieRequestsPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'uploaded-files',
                Component: UploadedFilesPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'product-import',
                Component: ProductImportPageSP,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'import-history',
                Component: ImportHistoryPageSP,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'all-payment-methods/:stax_customer_table_id',
                Component: PaymentMethodListPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'add-payment-method/:stax_customer_table_id',
                Component: AddPaymentMethodPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'all-subscriptions/:stax_customer_table_id',
                Component: SubscriptionsListPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'add-subscription/:stax_customer_table_id',
                Component: AddSubscriptionPage,
                errorElement: <SomethingWentWrong />,
              },
            ],
          },
          {
            path: 'owner',
            children: [
              {
                path: 'dashboard',
                Component: DashboardPageStoreOwner,
                errorElement: <SomethingWentWrong />,
              },
              { path: 'profile', Component: ProfilePage, errorElement: <SomethingWentWrong /> },
              {
                path: 'qr-scan-activity',
                Component: QRScanActivityPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'employee-management',
                Component: EmployeeManagementPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'vendors',
                children: [
                  {
                    index: true,
                    Component: ProductCatalogPage,
                    errorElement: <SomethingWentWrong />,
                  },
                  {
                    path: 'import-history',
                    Component: ImportHistoryOwnerPage,
                    errorElement: <SomethingWentWrong />,
                  },
                  {
                    path: 'product-import',
                    Component: ProdductImportPage,
                    errorElement: <SomethingWentWrong />,
                  },
                ],
              },
              {
                path: 'product-category',
                Component: ProductCategoryManagementPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'product-vendor',
                Component: ProductVendorManagementPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'product-tag',
                Component: ProductTagManagementPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'print-qr',
                Component: QRCodeManagementPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'qr-code-generator/settings',
                Component: QRCodeGeneratorSettingsPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'add-qr-print',
                Component: AddQrCodeQueue,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'qr-reprint',
                Component: RePrintQrCodes,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'batch-history',
                Component: BatchHistory,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'print-history',
                Component: PrintHistory,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'landing-page-configuration',
                Component: LandingPageConfiguration,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'landing-preview/:store_location_id',
                Component: LandingPreview,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'quote-and-lead',
                Component: QuoteAndLeadPage,
                errorElement: <SomethingWentWrong />,
              },
              { path: 'billing', Component: BillingPage, errorElement: <SomethingWentWrong /> },
              {
                path: 'reports',
                Component: QrScanAnalyticsPage,
                errorElement: <SomethingWentWrong />,
              },
            ],
          },
          {
            path: 'manager',
            children: [
              {
                path: 'dashboard',
                Component: DashboardPageStoreManager,
                errorElement: <SomethingWentWrong />,
              },
              { path: 'profile', Component: ProfilePage, errorElement: <SomethingWentWrong /> },
              {
                path: 'qr-scan-activity',
                Component: QRScanActivityPageSM,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'vendors',
                children: [
                  {
                    index: true,
                    Component: ProductCatalogPageSM,
                    errorElement: <SomethingWentWrong />,
                  },
                  {
                    path: 'import-history',
                    Component: ImportHistoryManagerPage,
                    errorElement: <SomethingWentWrong />,
                  },
                  {
                    path: 'product-import',
                    Component: ProductImportPageM,
                    errorElement: <SomethingWentWrong />,
                  },
                ],
              },
              {
                path: 'print-qr',
                Component: QRCodeManagementPageManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'qr-code-generator/settings',
                Component: QRCodeGeneratorSettingsPageManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'batch-history',
                Component: BatchHistoryManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'print-history',
                Component: PrintHistoryManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'qr-reprint',
                Component: RePrintQrCodesByManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'add-qr-print',
                Component: AddQrCodeQueueManagerPage,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'landing-page-configuration',
                Component: LandingPageConfigurationForManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'landing-preview/:store_location_id',
                Component: LandingPreviewManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'quote-and-lead',
                Component: QuoteAndLeadPageManager,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'employee-management',
                Component: EmployeeManagementPageSM,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'reports',
                Component: QrScanAnalyticsPageForManager,
                errorElement: <SomethingWentWrong />,
              },
            ],
          },
          {
            path: 'employee',
            children: [
              {
                path: 'dashboard',
                Component: DashboardPageEmployee,
                errorElement: <SomethingWentWrong />,
              },
              { path: 'profile', Component: ProfilePage, errorElement: <SomethingWentWrong /> },
              {
                path: 'qr-scan-activity',
                Component: QRScanActivityPageEmployee,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'product-lookup',
                Component: ProductCatalogEmployee,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'print-qr',
                Component: QRCodeManagementPageEmployee,
                errorElement: <SomethingWentWrong />,
              },
              {
                path: 'reports',
                Component: ReportForEmployee,
                errorElement: <SomethingWentWrong />,
              },
            ],
          },
        ],
      },
      // --- Pages not needed ---
      { path: 'blank', Component: BlankPage, errorElement: <SomethingWentWrong /> },
    ],
  },

  { path: '*', Component: ErrorPage },
]);

export default router;
